"use client";
import Icon from "@/components/icons/Icons";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import FundCardComponent from "../cards/FundCardComponent";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";

type FundItem = {
  id?: number;
  scheme_name?: string;
  scheme_code?: string;
  amc_code?: string;
  amc?: string;
  url?: string;
  category_type?: string;
  asset_id?: number;
  sip_allowed?: number;
  lump_allowed?: number;
  minimum_sip_invest?: number;
  minimum_investment?: number;
  six_month_abs?: number;
  one_year_abs?: number;
  three_year?: number;
};

const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0,
  }),
};

type ComponentProps = {
  direction: number;
  schemeData: FundItem[];
  error: boolean;
  investType: "sip" | "lumpsum";
  onSelectFund: (fund: FundItem | null) => void;
  selectedFund: FundItem | null;
};

export default function InvestGoldFormStep2({
  direction,
  schemeData,
  error,
  investType,
  onSelectFund,
  selectedFund,
}: ComponentProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedAMCs, setSelectedAMCs] = useState<string[]>([]);
  const [selectedAmount, setSelectedAmount] = useState<string | null>(null);
  const [tempSelectedAMCs, setTempSelectedAMCs] = useState<string[]>([]);
  const [tempSelectedAmount, setTempSelectedAmount] = useState<string | null>(
    null,
  );
  const [shouldReorder, setShouldReorder] = useState(false);
  const [filteredItems, setFilteredItems] = useState(0);

  const amcList = useMemo(() => {
    const map = new Map();
    schemeData.forEach((item) => {
      if (item.amc && item.amc_code) {
        map.set(item.amc_code, item.amc);
      }
    });
    return Array.from(map, ([amc_code, amc]) => ({ amc_code, amc }));
  }, [schemeData]);

  const filteredData = useMemo(() => {
    let data = [...schemeData];

    if (selectedAMCs.length > 0) {
      data = data.filter((item) => selectedAMCs.includes(item.amc_code!));
    }

    if (selectedAmount) {
      const [min, max] = selectedAmount.split("-").map(Number);

      data = data.filter((item) => {
        if (investType === "sip") {
          const sip = item.minimum_sip_invest ?? 0;
          return sip >= min && sip <= max;
        } else {
          const lump = item.minimum_investment ?? 0;
          return lump >= min && lump <= max;
        }
      });
    }

    if (search.trim() !== "") {
      const q = search.toLowerCase();
      data = data.filter((item) => item.scheme_name?.toLowerCase().includes(q));
    }

    return data;
  }, [schemeData, selectedAMCs, selectedAmount, investType, search]);

  const viewData = useMemo(() => {
    if (!shouldReorder || !selectedFund) return filteredData;

    const selected = filteredData.find(
      (i) => i.scheme_code === selectedFund.scheme_code,
    );
    if (!selected) return filteredData;

    const remaining = filteredData.filter(
      (i) => i.scheme_code !== selectedFund.scheme_code,
    );

    return [selected, ...remaining];
  }, [filteredData, selectedFund, shouldReorder]);

  useEffect(() => {
    setShouldReorder(true);
  }, []);

  useEffect(() => {
    const stillExists = filteredData.some(
      (f) => f.scheme_code === selectedFund?.scheme_code,
    );

    if (!stillExists) onSelectFund(null);
  }, [filteredData]);

  if (!schemeData) return null;

  const totalCols = Math.ceil(filteredData.length / 2);

  const resetFilters = () => {
    setTempSelectedAMCs([]);
    setTempSelectedAmount(null);

    setSelectedAMCs([]);
    setSelectedAmount(null);
    setSearch("");
    setFilteredItems(0);
  };

  const openFilterDrawer = () => {
    setTempSelectedAMCs(selectedAMCs);
    setTempSelectedAmount(selectedAmount);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setSelectedAMCs(tempSelectedAMCs);
    setSelectedAmount(tempSelectedAmount);
    setSearch("");
    setShouldReorder(true);
    setIsFilterOpen(false);

    const selectedAMCCount = tempSelectedAMCs.length;
    const selectedAmountCount = tempSelectedAmount ? 1 : 0;

    const totalSelected = selectedAMCCount + selectedAmountCount;
    setFilteredItems(totalSelected);
  };

  return (
    <>
      <motion.div
        key="step2"
        variants={stepVariants}
        custom={direction}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="w-full pb-5 xs:border-b border-[#D9D9D9] relative"
      >
        <div className="flex flex-col xs:flex-row xs:items-center gap-2 mb-4.5 xs:mb-3 px-5 xs:px-6">
          <div className="text-lg sm:text-xl font-semibold text-white shrink-0">
            Select Your SIP Scheme
          </div>

          <div className="flex items-center xs:ml-auto gap-2">
            <label className="group w-full pl-7 pr-2 xs:px-4 py-1 xs:py-2 max-w-48 rounded-lg xs:rounded-xl bg-white/5 transition border border-white/40 placeholder-white/60 hover:border-white/70 outline-hidden relative">
              <Icon.searchIcon className="w-5 h-5 xs:w-6 xs:h-6 absolute left-1.5 xs:left-3 top-1/2 -translate-y-1/2 pointer-events-none transition text-white/60 group-hover:text-white" />
              <input
                type="text"
                placeholder="Search Funds..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setShouldReorder(true);
                }}
                className="bg-transparent text-2xs leading-6 xs:text-2sm xs:leading-5.5 xs:px-6 w-full transition text-white/60 group-hover:text-white"
              />
            </label>

            {/* FILTER BUTTON */}
            <button
              onClick={openFilterDrawer}
              aria-label="Filter Drawer Button"
              className="group relative p-2 flex items-center gap-3 border border-white/40 hover:border-white/70 rounded-xl transition"
            >
              <Icon.filterBtnIcon className="w-4.5 h-4.5 xs:w-5.5 xs:h-5.5 transition text-white/60 group-hover:text-white" />
              <span className="bg-[#c00000] text-white rounded-full flex items-center justify-center w-4.5 h-4.5 text-xs leading-1 absolute -top-1.5 -right-1.5">
                {filteredItems}
              </span>
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div className="w-full relative h-66.5">
          {viewData.length === 0 ? (
            <div className="text-center text-primaryBlack text-lg font-semibold py-20">
              No Funds Found
            </div>
          ) : (
            <>
              {/* Swiper */}
              <Swiper
                modules={[Navigation]}
                spaceBetween={0}
                onSwiper={(s) => (swiperRef.current = s)}
                onSlideChange={(s) => setActiveIndex(s.activeIndex)}
                speed={500}
                allowTouchMove={true}
                slidesPerView="auto"
                className="pb-12"
              >
                {Array.from({ length: totalCols }).map((_, colIndex) => {
                  const card1 = viewData[colIndex * 2];
                  const card2 = viewData[colIndex * 2 + 1];
                  const isLastColumn = colIndex === totalCols - 1;
                  const columnPadding = isLastColumn ? "px-4" : "pl-4";

                  return (
                    <SwiperSlide key={colIndex} className="max-w-fit">
                      <div
                        className={`flex flex-col gap-4 py-2 ${columnPadding}`}
                      >
                        {card1 && (
                          <FundCardComponent
                            fund={card1}
                            isSelected={
                              selectedFund?.scheme_code === card1.scheme_code
                            }
                            investType={investType}
                            onSelect={() => {
                              onSelectFund(card1);
                              setShouldReorder(false);
                            }}
                          />
                        )}
                        {card2 && (
                          <FundCardComponent
                            fund={card2}
                            isSelected={
                              selectedFund?.scheme_code === card2.scheme_code
                            }
                            investType={investType}
                            onSelect={() => {
                              onSelectFund(card2);
                              setShouldReorder(false);
                            }}
                          />
                        )}
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {/* LEFT */}
          <button
            onClick={() => swiperRef.current?.slidePrev()}
            disabled={activeIndex === 0}
            aria-label="Previous Button"
            className={`group border border-white rounded-full w-8 h-8 sm:w-7 sm:h-7 flex items-center justify-center transition duration-300
          ${activeIndex === 0 ? "opacity-50 cursor-not-allowed" : "opacity-100 hover:border-[#C89900]"}`}
          >
            <Icon.closeSearchIcon className="w-4 h-4 transition duration-300 text-white group-hover:text-[#C89900] rotate-270" />
          </button>

          {/* RIGHT */}
          <button
            onClick={() => swiperRef.current?.slideNext()}
            disabled={activeIndex >= totalCols - 2}
            aria-label="Next Button"
            className={`group border border-white rounded-full w-8 h-8 sm:w-7 sm:h-7 flex items-center justify-center transition duration-300
          ${activeIndex >= totalCols - 2 ? "opacity-50 cursor-not-allowed" : "opacity-100 hover:border-[#C89900]"}`}
          >
            <Icon.closeSearchIcon className="mt-px w-4 h-4 transition duration-300 text-white group-hover:text-[#C89900] rotate-90" />
          </button>
        </div>

        {error && (
          <p className="absolute -bottom-3 xs:-bottom-6 inset-x-0 text-[#FF6565] text-2xs text-center">
            *Please select a fund to proceed.
          </p>
        )}
      </motion.div>

      <AnimatePresence mode="wait">
        {isFilterOpen && (
          <>
            {/* Background overlay */}
            <motion.div
              className="absolute inset-0 bg-primaryBlack/20 backdrop-blur-xs shadow-[-6px_0px_20px_0px_#00000030] z-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ ease: "easeInOut", duration: 0.35 }}
              onClick={() => setIsFilterOpen(false)}
            />

            {/* Drawer Panel */}
            <motion.div
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ ease: "easeInOut", duration: 0.3 }}
              className="absolute inset-y-0 right-0 h-full w-5/6 xs:w-full xs:max-w-lg bg-[#3a3a3a] border-l border-[#C89900] pl-3.5 pr-1 py-3 xs:p-5 z-2 shadow-xl"
            >
              <div className="absolute inset-x-0 top-0 p-3.5 bg-[#3a3a3a] border-b border-white flex justify-between items-center">
                <p className="text-white text-lg font-semibold">Filters</p>
                <button
                  onClick={() => setIsFilterOpen(false)}
                  aria-label="Filter drawer close button"
                >
                  <Icon.popupCloseIcon className="w-5 h-5 xs:w-6 xs:h-6 text-white hover:text-[#ffd90e] rotate-45 transition" />
                </button>
              </div>

              <div className="max-h-full pt-12 pb-28 text-white overflow-y-auto scrollbar-xs xs:scrollbar-md pr-1">
                {/* ⭐ AMC LIST */}
                <p className="text-sm xs:text-base font-semibold mb-3">
                  AMC List
                </p>

                <div className="flex flex-wrap gap-2.5 xs:gap-3 mb-8">
                  {amcList.map(({ amc, amc_code }) => (
                    <label
                      key={amc_code}
                      className={`flex items-center gap-2 px-2 py-1 xs:px-3 xs:py-1.5 text-white border rounded-lg text-xs xs:text-sm transition duration-300 cursor-pointer ${tempSelectedAMCs.includes(amc_code) ? "border-[#C89900] bg-[#C89900]/20" : "border-white/20 bg-transparent hover:border-[#C89900]"}`}
                    >
                      <input
                        type="checkbox"
                        checked={tempSelectedAMCs.includes(amc_code)}
                        className="accent-[#C89900] w-3 xs:w-4"
                        onChange={() => {
                          setTempSelectedAMCs((prev) =>
                            prev.includes(amc_code)
                              ? prev.filter((c) => c !== amc_code)
                              : [...prev, amc_code],
                          );
                        }}
                      />
                      {amc}
                    </label>
                  ))}
                </div>

                {/* ⭐ MINIMUM INVESTMENT (RADIO - SINGLE SELECT) */}
                <p className="text-sm xs:text-base font-semibold mb-3">
                  Minimum Investment
                </p>

                <div className="flex flex-wrap gap-2.5 xs:gap-3">
                  {[
                    { label: "₹0 – ₹500", value: "0-500" },
                    { label: "₹500 – ₹1000", value: "500-1000" },
                    { label: "₹1000 – ₹5000", value: "1000-5000" },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2 px-2 py-1 xs:px-3 xs:py-1.5 text-white border rounded-lg text-xs xs:text-sm transition duration-300 cursor-pointer ${tempSelectedAmount === opt.value ? "border-[#C89900] bg-[#C89900]/20" : "border-white/20 bg-transparent hover:border-[#C89900]"}`}
                    >
                      <input
                        type="radio"
                        name="minInvest"
                        className="accent-[#C89900] w-3 xs:w-4"
                        checked={tempSelectedAmount === opt.value}
                        onChange={() => setTempSelectedAmount(opt.value)}
                      />{" "}
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="absolute inset-x-0 bottom-0 px-5 py-4 bg-[#3a3a3a] flex items-center gap-5">
                <button
                  onClick={resetFilters}
                  className="w-1/3 py-1.5 xs:py-2 text-sm xs:text-base rounded-full border border-[#C89900] text-white font-semibold transition duration-300 hover:bg-[#c8990036]"
                >
                  Reset
                </button>

                <button
                  onClick={applyFilters}
                  className="w-3/5 py-1.5 xs:py-2 text-sm xs:text-base rounded-full bg-[#C89900] border border-[#C89900] text-white font-semibold shine-button-hover"
                >
                  Apply Filters
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// breakpoints={{
//   0: {
//     slidesPerView: 1.2,
//   },
//   420: {
//     slidesPerView: 1.4,
//   },
//   480: {
//     slidesPerView: 1.5,
//   },
//   631: {
//     slidesPerView: 2,
//   },
//   768: {
//     slidesPerView: 2.4,
//   },
// }}
